<template>
    <AmModal
        :open="open"
        size="narrow"
        title="Мои файлы"
        subtitle="PDF-экспорты ваших диалогов. Файлы хранятся в личном облаке."
        @close="onClose"
    >
        <div v-if="loading" class="state">Загружаю…</div>

        <div v-else-if="items.length === 0" class="state">
            Пока пусто. Экспортируйте диалог из меню «…» — файл появится здесь.
        </div>

        <ul v-else class="exports-list">
            <li
                v-for="x in items"
                :key="x.id"
                class="export-row"
            >
                <div class="export-main">
                    <div class="export-title">{{ x.title }}</div>
                    <div class="export-meta">
                        <span class="meta-badge">PDF</span>
                        <span>{{ formatSize(x.sizeBytes) }}</span>
                        <span v-if="x.createdAt">· {{ formatDate(x.createdAt) }}</span>
                        <span class="filename">· {{ x.filename }}</span>
                    </div>
                </div>
                <div class="export-actions">
                    <button
                        type="button"
                        class="icon-btn"
                        title="Скачать"
                        :disabled="busyId === x.id"
                        @click="onDownload(x)"
                    >
                        <ChatIcon name="download" :size="13" />
                    </button>
                    <button
                        type="button"
                        class="icon-btn icon-btn--danger"
                        title="Удалить"
                        :disabled="busyId === x.id"
                        @click="onDelete(x)"
                    >
                        <ChatIcon name="trash" :size="13" />
                    </button>
                </div>
            </li>
        </ul>
    </AmModal>
</template>

<script setup>
/*
 * Модалка «Мои файлы» — список PDF-экспортов диалогов текущего пользователя.
 *
 * Скачивание идёт по короткоживущему presigned URL (S3). URL не кэшируем в
 * localStorage — при каждом клике дёргаем /exports/{id}/url заново: TTL 15
 * минут; если пользователь передумал и вернулся через полчаса, ссылка была
 * бы уже мёртвой.
 */
import { ref, watch } from 'vue'
import AmModal from '@/components/ui/AmModal.vue'
import ChatIcon from '@/components/chat/icons/ChatIcon.vue'
import { getMyExports, getExportUrl, deleteExport } from '@/utils/chatApi'

const props = defineProps({
    open: { type: Boolean, default: false },
})

const emit = defineEmits(['close'])

const loading = ref(false)
const items = ref([])
const busyId = ref(null)

watch(
    () => props.open,
    async (isOpen) => {
        if (!isOpen) return
        await load()
    },
)

async function load() {
    loading.value = true
    try {
        items.value = await getMyExports()
    } catch {
        items.value = []
    } finally {
        loading.value = false
    }
}

function formatDate(iso) {
    if (!iso) return ''
    return new Date(iso).toLocaleString('ru-RU', {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    })
}

function formatSize(bytes) {
    const n = Number(bytes) || 0
    if (n < 1024) return `${n} Б`
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} КБ`
    return `${(n / 1024 / 1024).toFixed(1)} МБ`
}

async function onDownload(x) {
    busyId.value = x.id
    try {
        // Отдаём presigned URL и открываем его напрямую. S3 отдаёт файл сам,
        // без нагрузки на PHP-fpm. Content-Disposition проставлен в объекте
        // при заливке (см. ChatExportService::store → 'ContentDisposition'),
        // поэтому имя файла в диалоге «Сохранить как» подтянется корректно.
        //
        // ⚠️ Локальный dev: MinIO внутри docker-сети под хостом `minio:9000`,
        // presigned URL c ним из браузера НЕ откроется. Если ловите такую
        // ссылку — надо либо пробросить `minio` в /etc/hosts на 127.0.0.1
        // (тогда браузер попадёт в проброшенный порт из compose), либо
        // прописать в .env публично доступный endpoint MinIO.
        const { url, filename } = await getExportUrl(x.id)
        if (!url) throw new Error('empty_url')

        const a = document.createElement('a')
        a.href = url
        if (filename) a.download = filename
        a.rel = 'noopener'
        document.body.appendChild(a)
        a.click()
        a.remove()
    } catch (e) {
        if (e?.status === 404) {
            alert('Файл больше недоступен. Попробуйте пересоздать экспорт.')
        } else {
            alert('Не удалось скачать файл. Попробуйте ещё раз.')
        }
    } finally {
        busyId.value = null
    }
}

async function onDelete(x) {
    if (!confirm(`Удалить файл «${x.title || x.filename}»? Это действие необратимо.`)) return
    busyId.value = x.id
    try {
        await deleteExport(x.id)
        items.value = items.value.filter(i => i.id !== x.id)
    } catch (e) {
        alert('Не удалось удалить файл.')
    } finally {
        busyId.value = null
    }
}

function onClose() { emit('close') }
</script>

<style scoped>
.state {
    padding: 28px 4px;
    text-align: center;
    color: #888888;
    font-size: 13px;
}

.exports-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
}

.export-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    padding: 10px 4px;
    border-radius: 8px;
}
.export-row:hover { background: #f7f7f7; }

.export-main {
    min-width: 0;
}

.export-title {
    font-size: 13px;
    font-weight: 600;
    color: #23282C;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.export-meta {
    font-size: 11px;
    color: #888888;
    margin-top: 2px;
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
}
.filename {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    color: #5f5f5f;
    max-width: 240px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.meta-badge {
    background: #EEE9FF;
    color: #4433B8;
    padding: 1px 6px;
    border-radius: 3px;
    font-weight: 600;
    font-size: 10px;
    text-transform: uppercase;
}

.export-actions {
    display: flex;
    gap: 6px;
    flex-shrink: 0;
}

.icon-btn {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    border: 1px solid #e0e0e0;
    background: white;
    color: #23282C;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}
.icon-btn:hover:not(:disabled) { border-color: #23282C; }
.icon-btn:disabled { opacity: 0.55; cursor: not-allowed; }

.icon-btn--danger:hover:not(:disabled) {
    color: #C0050F;
    border-color: #C0050F;
}
</style>
